import { FC, useCallback } from 'react';
import Highcharts from 'highcharts';
import Chart from 'highcharts-react-official';
import { InlineLoading } from '@carbon/react';
import { themed, CHART_PALETTE } from '../chart-theme';
import { fetchDurationHistogram, type HistogramFilters } from './api';
import { useAgentResource } from './use-agent-resource';
import type { DurationBucket } from './types';
import styles from './page.module.scss';

interface DurationHistogramProps {
  agentType: string;
  filters: HistogramFilters;
  selectedLabel: string | null;
  onSelectBucket: (bucket: DurationBucket | null) => void;
}

export const DurationHistogram: FC<DurationHistogramProps> = ({
  agentType,
  filters,
  selectedLabel,
  onSelectBucket,
}) => {
  const { status, model, start, end } = filters;
  const { data, loading, error } = useAgentResource(
    useCallback(
      (controller: AbortController) =>
        fetchDurationHistogram(
          agentType,
          { status, model, start, end },
          controller
        ),
      [agentType, status, model, start, end]
    ),
    [agentType, status, model, start, end]
  );
  const buckets = data?.data ?? [];

  if (loading) return <InlineLoading description="Loading distribution..." />;
  if (error) return <div className={styles.detailError}>{error}</div>;
  if (!buckets.length) return null;

  // Per-bar color: palette by index, dimmed when another bucket is selected.
  const points = buckets.map((b, i) => {
    const base = CHART_PALETTE[i % CHART_PALETTE.length];
    const dimmed = selectedLabel !== null && b.label !== selectedLabel;
    return {
      y: b.count,
      color: dimmed ? Highcharts.color(base).setOpacity(0.3).get('rgba') : base,
    };
  });

  return (
    <Chart
      highcharts={Highcharts}
      options={themed({
        chart: { type: 'column', height: 240 },
        xAxis: {
          categories: buckets.map((b) => b.label),
          title: { text: 'run duration' },
        },
        yAxis: { min: 0, title: { text: 'runs' }, allowDecimals: false },
        legend: { enabled: false },
        tooltip: { headerFormat: '', pointFormat: '<b>{point.y}</b> runs' },
        plotOptions: {
          column: {
            borderRadius: 2,
            borderWidth: 0,
            cursor: 'pointer',
            point: {
              events: {
                click(this: Highcharts.Point) {
                  const bucket = buckets[this.index];
                  if (!bucket) return;
                  onSelectBucket(
                    bucket.label === selectedLabel ? null : bucket
                  );
                },
              },
            },
          },
        },
        series: [
          { type: 'column', name: 'runs', data: points },
        ] as Highcharts.SeriesOptionsType[],
      })}
    />
  );
};
